// src/features/workflows/GuidedLaunchFlow.jsx
//
// TASK-4584: Guided Launch — 5-step wizard for starting a new workflow run.
// Steps: 1) choose template  2) basic info  3) review & start  4) confirmation
// 5) hands off to Run Detail (track progress) via onLaunched.
//
// editor/admin only — WorkflowsPage gates the entry point; this component
// assumes it is only ever mounted for a role allowed to create runs.

import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Workflow, Loader2 } from "lucide-react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { getWorkflowTemplates, createWorkflowRun } from "../../data/workflowsClient.js";
import { getLocations } from "../../data/locationsClient.js";
import { log } from "../../utils/logger.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";

const STEPS = [
  { key: "template", label: "Choose template" },
  { key: "info",     label: "Basic info" },
  { key: "review",   label: "Review & start" },
  { key: "done",     label: "Confirmation" },
];

function StepDots({ stepIndex }) {
  return (
    <div data-testid="guided-launch-stepper" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
      {STEPS.map((s, i) => {
        const done = i < stepIndex;
        const active = i === stepIndex;
        return (
          <React.Fragment key={s.key}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                data-testid={`guided-launch-step-dot-${s.key}`}
                style={{
                  width: 22, height: 22, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: T.fsXxs, fontWeight: T.fwBold,
                  background: done ? T.green : active ? T.blue : T.bg,
                  color: done || active ? T.white : T.mid,
                  border: active ? `2px solid ${T.blue}` : `1px solid ${T.border}`,
                }}
              >
                {done ? <Check size={12} /> : i + 1}
              </div>
              <span style={{ fontSize: T.fsXs, fontWeight: active ? T.fwBold : T.fwRegular, color: active ? T.dark : T.mid }}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 1, background: T.border, minWidth: 16 }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function parseParticipants(text) {
  return String(text || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function fieldStyle() {
  return {
    width: "100%", boxSizing: "border-box", padding: "8px 10px",
    fontSize: T.fsSm5, border: `1px solid ${T.border}`, borderRadius: 7,
    color: T.dark, background: T.white,
  };
}

// WORKFLOWS-SLICE-2-LAUNCH-FROM-OBJECT: `source` pre-fills the flow when it
// is launched from a Decision/Location/Lease/Market/Signal object instead of
// the bare Workflows page. Shape: { type, id, label, locationId?, templateHint? }.
//   - label is stored on the run as source_label (shown as "Started from X").
//   - locationId (when the source itself is location-scoped, e.g. a Lease or
//     Location) pre-fills the Location field.
//   - templateHint (a substring to match against template name) auto-selects
//     the template and skips straight to the Basic info step when exactly one
//     template matches; falls back to normal manual selection otherwise.
export function GuidedLaunchFlow({ onCancel, onLaunched, source = null }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [templateId, setTemplateId] = useState(null);
  const [form, setForm] = useState({
    name: source?.label ? `${source.label} - action` : "",
    ownerName: "", requestorName: "", dueDate: "", reason: "", details: "",
    locationId: source?.locationId || "", participantsText: "",
  });
  const [createdRun, setCreatedRun] = useState(null);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    getWorkflowTemplates()
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
        if (source?.templateHint) {
          const hint = String(source.templateHint);
          const matches = list.filter((t) => matchesFoldedQuery(hint, t.name));
          if (matches.length === 1) {
            setTemplateId(matches[0].id);
            setStepIndex(1);
          }
        }
      })
      .catch((err) => { if (!cancelled) { log.error("GuidedLaunchFlow: failed to load templates", err); setError(err); } })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    getLocations()
      .then((list) => { if (!cancelled) setLocations(Array.isArray(list) ? list : []); })
      .catch((err) => { log.error("GuidedLaunchFlow: failed to load locations", err); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTemplate = templates.find((t) => t.id === templateId) || null;

  const canProceedTemplate = !!templateId;
  const canProceedInfo = !!form.name.trim();

  const goNext = useCallback(() => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1)), []);
  const goBack = useCallback(() => setStepIndex((i) => Math.max(i - 1, 0)), []);

  async function handleStart() {
    setSubmitting(true);
    setError(null);
    try {
      const run = await createWorkflowRun({
        templateId,
        name: form.name.trim(),
        ownerName: form.ownerName.trim() || null,
        requestorName: form.requestorName.trim() || null,
        dueDate: form.dueDate || null,
        reason: form.reason.trim() || null,
        details: form.details.trim() || null,
        locationId: form.locationId || null,
        participants: parseParticipants(form.participantsText),
        source: source ? { type: source.type, id: source.id, label: source.label } : undefined,
      });
      setCreatedRun(run);
      setStepIndex(3);
    } catch (err) {
      log.error("GuidedLaunchFlow: failed to create run", err);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  const currentStep = STEPS[stepIndex].key;

  return (
    <div data-testid="guided-launch-flow" style={{ maxWidth: 640, margin: "0 auto" }}>
      <StepDots stepIndex={stepIndex} />

      {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}

      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 22, minHeight: 260 }}>
        {currentStep === "template" && (
          <div data-testid="guided-launch-step-template">
            <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>Choose a template</div>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>Pick the process you want to launch a run of.</div>
            {templatesLoading ? (
              <div style={{ fontSize: T.fsSm, color: T.mid }}>Loading templates…</div>
            ) : templates.length === 0 ? (
              <div style={{ fontSize: T.fsSm, color: T.mid }}>No templates available yet - create one in the Library.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {templates.map((t) => {
                  const active = t.id === templateId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`guided-launch-template-${t.id}`}
                      onClick={() => setTemplateId(t.id)}
                      style={{
                        textAlign: "left", cursor: "pointer",
                        border: active ? `2px solid ${T.blue}` : `1px solid ${T.border}`,
                        background: active ? T.blueSoft : T.white,
                        borderRadius: 9, padding: "10px 14px",
                        display: "flex", alignItems: "center", gap: 10,
                      }}
                    >
                      <Workflow size={16} color={active ? T.blue : T.mid} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>{t.name}</div>
                        {t.description && <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>{t.description}</div>}
                      </div>
                      <div style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap" }}>
                        {t.phaseCount ?? 0} phase{(t.phaseCount ?? 0) === 1 ? "" : "s"}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {currentStep === "info" && (
          <div data-testid="guided-launch-step-info">
            <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>Basic information</div>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>Launching <strong>{selectedTemplate?.name}</strong></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ fontSize: T.fsXs, color: T.mid }}>
                Run name *
                <input
                  data-testid="guided-launch-input-name"
                  style={{ ...fieldStyle(), marginTop: 4 }}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Jane Doe - New Hire Setup"
                />
              </label>
              <div style={{ display: "flex", gap: 12 }}>
                <label style={{ fontSize: T.fsXs, color: T.mid, flex: 1 }}>
                  Owner
                  <input
                    data-testid="guided-launch-input-owner"
                    style={{ ...fieldStyle(), marginTop: 4 }}
                    value={form.ownerName}
                    onChange={(e) => setForm((f) => ({ ...f, ownerName: e.target.value }))}
                  />
                </label>
                <label style={{ fontSize: T.fsXs, color: T.mid, flex: 1 }}>
                  Requestor
                  <input
                    data-testid="guided-launch-input-requestor"
                    style={{ ...fieldStyle(), marginTop: 4 }}
                    value={form.requestorName}
                    onChange={(e) => setForm((f) => ({ ...f, requestorName: e.target.value }))}
                  />
                </label>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <label style={{ fontSize: T.fsXs, color: T.mid, flex: 1 }}>
                  Location
                  <select
                    data-testid="guided-launch-input-location"
                    style={{ ...fieldStyle(), marginTop: 4 }}
                    value={form.locationId}
                    onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
                  >
                    <option value="">Unassigned</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name || loc.locationAlias || loc.id}</option>
                    ))}
                  </select>
                </label>
                <label style={{ fontSize: T.fsXs, color: T.mid, flex: 1 }}>
                  Target date
                  <input
                    type="date"
                    data-testid="guided-launch-input-duedate"
                    style={{ ...fieldStyle(), marginTop: 4 }}
                    value={form.dueDate}
                    onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                  />
                </label>
              </div>
              <label style={{ fontSize: T.fsXs, color: T.mid }}>
                Participants
                <input
                  data-testid="guided-launch-input-participants"
                  style={{ ...fieldStyle(), marginTop: 4 }}
                  value={form.participantsText}
                  onChange={(e) => setForm((f) => ({ ...f, participantsText: e.target.value }))}
                  placeholder="Comma-separated names, e.g. Jane Doe, IT Team"
                />
              </label>
              <label style={{ fontSize: T.fsXs, color: T.mid }}>
                Reason
                <input
                  data-testid="guided-launch-input-reason"
                  style={{ ...fieldStyle(), marginTop: 4 }}
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                />
              </label>
              <label style={{ fontSize: T.fsXs, color: T.mid }}>
                Details
                <textarea
                  data-testid="guided-launch-input-details"
                  style={{ ...fieldStyle(), marginTop: 4, minHeight: 70, resize: "vertical", fontFamily: "inherit" }}
                  value={form.details}
                  onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
                />
              </label>
            </div>
          </div>
        )}

        {currentStep === "review" && (
          <div data-testid="guided-launch-step-review">
            <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>Review & start</div>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>Confirm the details below, then start the action.</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                ["Template", selectedTemplate?.name],
                ["Run name", form.name],
                ["Owner", form.ownerName || "Unassigned"],
                ["Requestor", form.requestorName || "—"],
                ["Location", (locations.find((l) => l.id === form.locationId)?.locationAlias) || (locations.find((l) => l.id === form.locationId)?.name) || form.locationId || "Unassigned"],
                ["Participants", parseParticipants(form.participantsText).join(", ") || "—"],
                ["Target date", form.dueDate || "—"],
                ["Reason", form.reason || "—"],
              ].map(([label, value]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", borderBottom: `1px solid ${T.border}`, paddingBottom: 6 }}>
                  <span style={{ fontSize: T.fsXs, color: T.mid }}>{label}</span>
                  <span style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>{value}</span>
                </div>
              ))}
              {form.details && (
                <div>
                  <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 3 }}>Details</div>
                  <div style={{ fontSize: T.fsSm5, color: T.dark }}>{form.details}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {currentStep === "done" && createdRun && (
          <div data-testid="guided-launch-step-done" style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: T.greenSoft, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <Check size={24} color={T.green} />
            </div>
            <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>Action started</div>
            <div style={{ fontSize: T.fsSm5, color: T.mid, marginBottom: 20 }}>"{createdRun.name}" is now in progress.</div>
            <button
              type="button"
              data-testid="guided-launch-track-progress"
              onClick={() => onLaunched(createdRun.id)}
              style={{
                background: T.blue, color: T.white, border: "none", borderRadius: 8,
                padding: "9px 20px", fontSize: T.fsSm5, fontWeight: T.fwBold, cursor: "pointer",
              }}
            >
              Track progress
            </button>
          </div>
        )}
      </div>

      {currentStep !== "done" && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
          <button
            type="button"
            data-testid="guided-launch-back"
            onClick={stepIndex === 0 ? onCancel : goBack}
            style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 16px", fontSize: T.fsSm5, color: T.dark, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          >
            <ArrowLeft size={14} /> {stepIndex === 0 ? "Cancel" : "Back"}
          </button>

          {currentStep === "review" ? (
            <button
              type="button"
              data-testid="guided-launch-start"
              onClick={handleStart}
              disabled={submitting}
              style={{ background: T.blue, color: T.white, border: "none", borderRadius: 8, padding: "8px 18px", fontSize: T.fsSm5, fontWeight: T.fwBold, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1, display: "flex", alignItems: "center", gap: 6 }}
            >
              {submitting ? <Loader2 size={14} className="spin" /> : null} Start action
            </button>
          ) : (
            <button
              type="button"
              data-testid="guided-launch-next"
              onClick={goNext}
              disabled={(currentStep === "template" && !canProceedTemplate) || (currentStep === "info" && !canProceedInfo)}
              style={{
                background: T.blue, color: T.white, border: "none", borderRadius: 8, padding: "8px 18px",
                fontSize: T.fsSm5, fontWeight: T.fwBold, cursor: "pointer",
                opacity: (currentStep === "template" && !canProceedTemplate) || (currentStep === "info" && !canProceedInfo) ? 0.5 : 1,
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              Next <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
